import { nanoid } from "nanoid";

export const ROOM_DISPLAY_MEDIA_KEY_PREFIX = "uploads/room-displays/";

export const ROOM_DISPLAY_VIDEO_MAX_BYTES = 50 * 1024 * 1024;
export const ROOM_DISPLAY_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const ROOM_DISPLAY_VIDEO_MAX_DURATION_SECONDS = 30;
export const ROOM_DISPLAY_CHUNK_THRESHOLD_BYTES = 5 * 1024 * 1024;

export const ROOM_DISPLAY_VIDEO_ACCEPT_MIME = [
  "video/mp4",
  "video/webm",
] as const;

export const ROOM_DISPLAY_IMAGE_ACCEPT_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export type RoomDisplayMediaType = "image" | "video";

export function roomDisplayMediaKeyPrefix(screenId: string): string {
  return `${ROOM_DISPLAY_MEDIA_KEY_PREFIX}${screenId}/`;
}

export function sanitizeRoomDisplayFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 120) || "media";
}

export function buildRoomDisplayMediaObjectKey(
  screenId: string,
  filename: string,
): string {
  const safe = sanitizeRoomDisplayFilename(filename.trim());
  return `${roomDisplayMediaKeyPrefix(screenId)}${nanoid()}-${safe}`;
}

export function isValidRoomDisplayMediaObjectKey(
  key: string,
  screenId: string,
): boolean {
  const prefix = roomDisplayMediaKeyPrefix(screenId);
  return key.startsWith(prefix) && key.length > prefix.length;
}

export function isAllowedRoomDisplayVideoContentType(
  contentType: string | undefined,
): boolean {
  if (!contentType?.trim()) return false;
  const mime = contentType.trim().toLowerCase();
  return (ROOM_DISPLAY_VIDEO_ACCEPT_MIME as readonly string[]).includes(mime);
}

export function isAllowedRoomDisplayImageContentType(
  contentType: string | undefined,
): boolean {
  if (!contentType?.trim()) return false;
  const mime = contentType.trim().toLowerCase();
  return (ROOM_DISPLAY_IMAGE_ACCEPT_MIME as readonly string[]).includes(mime);
}

export function isRoomDisplayVideoSizeAllowed(sizeBytes: number | undefined): boolean {
  if (sizeBytes === undefined) return false;
  return sizeBytes > 0 && sizeBytes <= ROOM_DISPLAY_VIDEO_MAX_BYTES;
}

export function isRoomDisplayImageSizeAllowed(sizeBytes: number | undefined): boolean {
  if (sizeBytes === undefined) return false;
  return sizeBytes > 0 && sizeBytes <= ROOM_DISPLAY_IMAGE_MAX_BYTES;
}

export function isRoomDisplayVideoDurationAllowed(
  durationSeconds: number | undefined,
): boolean {
  if (durationSeconds === undefined) return false;
  return (
    durationSeconds > 0 &&
    durationSeconds <= ROOM_DISPLAY_VIDEO_MAX_DURATION_SECONDS
  );
}

export function inferRoomDisplayMediaTypeFromKey(key: string): RoomDisplayMediaType {
  const lower = key.toLowerCase();
  if (lower.endsWith(".mp4") || lower.endsWith(".webm")) return "video";
  return "image";
}

export function mediaTypeMatchesKey(
  mediaType: RoomDisplayMediaType,
  key: string,
): boolean {
  const inferred = inferRoomDisplayMediaTypeFromKey(key);
  return inferred === mediaType;
}

export function shouldUseChunkUpload(sizeBytes: number): boolean {
  return sizeBytes > ROOM_DISPLAY_CHUNK_THRESHOLD_BYTES;
}
